import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { type FC, useState } from 'react';
import { Tooltip } from '~/commonComponents/TooltipWrapper/TooltipWrapper';
import isHoliday from '~/components/Request/isHoliday';
import BackTipsCard from '~/components/Request/multiday/components/BackTipsCard';
import { isDailyOvertime } from '~/components/Request/multiday/utils';
import { useAverageRate } from '~/domain/Jobs';
import { UserProfession } from '~/enums/UserProfession';
import { useAppSelector } from '~/hooks/useAppSelector';
import BulbIcon from '~/images/icons/BulbIcon';
import EditPencilIcon from '~/images/icons/EditPencil';
import TrashIcon from '~/images/icons/Trash';
import WarningSolidIcon from '~/images/icons/WarningSolid';
import { Colors } from '~/themes/colors';

interface IShiftReviewCardProps {
  date: Date;
  officeId: string;
  professionType: string;
  specialty: string;
  arrivalTime: string;
  departureTime: string;
  mealBreakDuration: string;
  isMealBreakPaid: boolean;
  rate: number;
  isAssistedHygiene?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const parseTimeToDate = (date: Date, time: string): Date => {
  const result = new Date(date);
  const match = time.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i);

  if (!match) return result;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toLowerCase();

  if (period === 'pm' && hours !== 12) hours += 12;
  if (period === 'am' && hours === 12) hours = 0;

  result.setHours(hours, minutes, 0, 0);
  return result;
};

export const ShiftReviewCard: FC<IShiftReviewCardProps> = ({
  date,
  officeId,
  professionType,
  specialty,
  arrivalTime,
  departureTime,
  mealBreakDuration,
  isMealBreakPaid,
  rate,
  isAssistedHygiene,
  onEdit,
  onDelete,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const { data: averageRating } = useAverageRate(officeId);
  const selectedChildOffice = useAppSelector((state) => state.user.selectedChildOffice);

  const professionCode = UserProfession.getCode(professionType) as string | undefined;
  const profRate = (professionCode && averageRating?.[professionCode]) || { average: 49 };
  const industrialAverage = Math.round((rate / profRate.average) * 100);

  const workerClassificationType = selectedChildOffice?.workerClassification?.classificationType;
  const overtimeLimits = workerClassificationType
    ? selectedChildOffice?.overtimeLimits?.[workerClassificationType]
    : undefined;

  const jobForOvertime = {
    localStart: arrivalTime,
    localEnd: departureTime,
    break_time: mealBreakDuration,
    is_break_paid: isMealBreakPaid,
  };

  const isOvertime = isDailyOvertime(jobForOvertime, overtimeLimits?.daily);

  const now = new Date();
  const shiftStart = parseTimeToDate(date, arrivalTime);
  const hoursUntilShift = (shiftStart.getTime() - now.getTime()) / (1000 * 60 * 60);
  const showTips = hoursUntilShift <= 36 && hoursUntilShift > 0 && industrialAverage <= 105;

  const formattedDate = format(date, 'EEE, MMM dd, yyyy');
  const holidayDate = new Date(date);
  holidayDate.setHours(8, 0, 0, 0);
  const isHolidayDate = isHoliday(holidayDate);

  const specialtyDisplay = specialty?.split('_')?.[1] || 'General Dentistry';

  const handleTipsClick = () => {
    setIsFlipped((prev) => !prev);
  };

  return (
    <motion.div
      animate={{ rotateY: isFlipped ? 180 : 0 }}
      transition={{ duration: 0.6 }}
      data-testid="shift-review-card"
      data-cy="shift-review-card"
      style={{
        width: 438,
        height: 256,
        position: 'relative',
        backgroundColor: Colors.white,
        borderRadius: 8,
        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 3px 10px 1px rgba(0, 0, 0, 0.15)',
        border: showTips
          ? `2px solid ${isFlipped ? Colors.secondary_25 : Colors.secondary_500}`
          : '2px solid transparent',
      }}
    >
      {!isFlipped && (
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: '100%',
          }}
        >
          {/* Header */}
          <div
            style={{
              borderBottom: `1px solid ${Colors.neutral_100}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              margin: '18px 16px',
              paddingBottom: 8,
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <span data-testid="review-date" data-cy="review-date" style={styles.titleStyle}>
                {formattedDate}
              </span>
              {isHolidayDate && (
                <span data-testid="holiday-badge" data-cy="holiday-badge" style={styles.holidayBadge}>
                  holiday
                </span>
              )}
            </div>

            <div style={{ marginRight: 10, display: 'flex', gap: 16 }}>
              <button
                type="button"
                style={{ all: 'unset', cursor: 'pointer' }}
                data-testid="edit-shift"
                data-cy="edit-shift"
                onClick={onEdit}
                aria-label="Edit shift"
              >
                <EditPencilIcon color={Colors.secondary_500} />
              </button>
              <button
                type="button"
                style={{ all: 'unset', cursor: 'pointer' }}
                data-testid="remove-shift"
                data-cy="remove-shift"
                onClick={onDelete}
                aria-label="Remove shift"
              >
                <TrashIcon color={Colors.secondary_500} />
              </button>
            </div>
          </div>

          {/* Body */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              margin: '0 26px 26px',
              gap: 2,
            }}
          >
            <span style={styles.textStyle}>{professionType}</span>
            <span style={styles.textStyle}>{specialtyDisplay}</span>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
              }}
            >
              <span
                style={{
                  ...styles.textStyle,
                  display: 'flex',
                  alignItems: 'center',
                  marginRight: 4,
                }}
              >
                {arrivalTime} - {departureTime}
                {isOvertime && (
                  <Tooltip text="Overtime">
                    <span style={{ marginLeft: 4, marginBottom: -3, display: 'flex' }}>
                      <WarningSolidIcon color={Colors.warning_500} />
                    </span>
                  </Tooltip>
                )}
              </span>
              <span style={styles.textStyle}>
                / Meal Break: {mealBreakDuration} {isMealBreakPaid ? '(Paid)' : '(Unpaid)'}
              </span>
            </div>
            {isAssistedHygiene !== undefined && (
              <span style={styles.textStyle}>
                Assisted Hygiene Available: {isAssistedHygiene ? 'Yes' : 'No'}
              </span>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {showTips && <BulbIcon color={Colors.secondary_500} />}
              <span data-testid="review-rate" data-cy="review-rate" style={styles.textStyle}>{`$${rate}/hr`}</span>
            </div>
            {showTips && (
              <div
                style={{
                  position: 'absolute',
                  right: 26,
                  bottom: 32,
                }}
              >
                <button
                  type="button"
                  data-testid="tips-button"
                  data-cy="tips-button"
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 4,
                    height: 30,
                    width: 85,
                    backgroundColor: Colors.secondary_500,
                    borderRadius: 24,
                    boxShadow:
                      '0px 1px 2px 0px rgba(0, 0, 0, 0.15), 0px 3px 10px 1px rgba(0, 0, 0, 0.15)',
                  }}
                  onClick={handleTipsClick}
                  aria-label="View tips"
                >
                  <BulbIcon />
                  <b
                    style={{
                      fontFamily: 'Nunito',
                      color: Colors.white,
                      fontSize: 16,
                      marginTop: 2,
                    }}
                  >
                    Tips
                  </b>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {isFlipped && (
        <div
          style={{
            transform: 'rotateY(180deg)',
            height: '100%',
          }}
        >
          <BackTipsCard localDate={date} onReturnClick={handleTipsClick} />
        </div>
      )}
    </motion.div>
  );
};

const styles = {
  titleStyle: {
    color: Colors.neutral_600,
    fontFamily: 'Nunito',
    fontWeight: 'bold' as const,
    fontSize: 18,
    marginLeft: 10,
    textTransform: 'capitalize' as const,
  },
  holidayBadge: {
    fontFamily: 'Nunito',
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase' as const,
    color: Colors.white,
    padding: '2px 7px',
    backgroundColor: Colors.neutral_400,
    borderRadius: 3,
  },
  textStyle: {
    color: Colors.neutral_600,
    fontFamily: 'Nunito',
    fontSize: 16,
    marginTop: 2,
  },
};
